import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import {
  Controller,
  FormProvider,
  useFormContext,
  type Control,
  type ControllerFieldState,
  type ControllerProps,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { cn } from "../../lib/utils";
import { Label } from "./label";
import {
  LookupSelect,
  Select,
  type LookupSelectProps,
  type SelectOption,
  type SelectProps,
} from "./select";

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formLabelId: `${id}-form-item-label`,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

function FormItem({ className, ...props }: React.ComponentProps<"div">) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div className={cn("space-y-2", className)} {...props} />
    </FormItemContext.Provider>
  );
}

function FormLabel({ className, ...props }: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const { error, formLabelId } = useFormField();

  return <Label id={formLabelId} className={cn(error && "text-destructive", className)} {...props} />;
}

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formLabelId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      id={formItemId}
      aria-labelledby={formLabelId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  );
}

function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
  const { formDescriptionId } = useFormField();

  return <p id={formDescriptionId} className={cn("text-muted-foreground text-sm", className)} {...props} />;
}

function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : props.children;

  if (!body) {
    return null;
  }

  return (
    <p id={formMessageId} className={cn("text-destructive text-sm", className)} {...props}>
      {body}
    </p>
  );
}

type FormFieldWrapperProps = {
  children: React.ReactNode;
  className?: string;
  controlClassName?: string;
  description?: React.ReactNode;
  descriptionClassName?: string;
  hideMessage?: boolean;
  label?: React.ReactNode;
  labelClassName?: string;
  message?: React.ReactNode;
  messageClassName?: string;
  required?: boolean;
};

function FormFieldWrapper({
  children,
  className,
  controlClassName,
  description,
  descriptionClassName,
  hideMessage = false,
  label,
  labelClassName,
  message,
  messageClassName,
  required = false,
}: FormFieldWrapperProps) {
  return (
    <FormItem className={className}>
      {label ? (
        <FormLabel className={labelClassName}>
          {label}
          {required ? (
            <span aria-hidden='true' className='text-destructive ml-1'>
              *
            </span>
          ) : null}
        </FormLabel>
      ) : null}
      <FormControl className={controlClassName}>{children}</FormControl>
      {description ? <FormDescription className={descriptionClassName}>{description}</FormDescription> : null}
      {hideMessage ? null : <FormMessage className={messageClassName}>{message}</FormMessage>}
    </FormItem>
  );
}

type FormControlledFieldRenderProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
};

type FormControlledFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  name: TName;
  className?: string;
  controlClassName?: string;
  description?: React.ReactNode;
  descriptionClassName?: string;
  hideMessage?: boolean;
  label?: React.ReactNode;
  labelClassName?: string;
  message?: React.ReactNode;
  messageClassName?: string;
  renderInput: (props: FormControlledFieldRenderProps<TFieldValues, TName>) => React.ReactNode;
  required?: boolean;
};

const FormControlledField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  className,
  controlClassName,
  description,
  descriptionClassName,
  hideMessage,
  label,
  labelClassName,
  message,
  messageClassName,
  renderInput,
  required,
}: FormControlledFieldProps<TFieldValues, TName>) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormFieldWrapper
          className={className}
          controlClassName={controlClassName}
          description={description}
          descriptionClassName={descriptionClassName}
          hideMessage={hideMessage}
          label={label}
          labelClassName={labelClassName}
          message={message}
          messageClassName={messageClassName}
          required={required}
        >
          {renderInput({ field, fieldState })}
        </FormFieldWrapper>
      )}
    />
  );
};

type FormSelectOption = SelectOption;

type FormSelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<FormControlledFieldProps<TFieldValues, TName>, "renderInput"> & {
  clearable?: boolean;
  mapValueToField?: (
    nextValue: string | string[],
    field: ControllerRenderProps<TFieldValues, TName>,
  ) => string | string[] | undefined;
  options?: FormSelectOption[];
  placeholder?: React.ReactNode;
  selectProps?: Omit<
    SelectProps<string>,
    "defaultValue" | "isClearable" | "onValueChange" | "options" | "placeholder" | "value"
  >;
};

const FormSelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  clearable = false,
  mapValueToField,
  options,
  placeholder,
  selectProps,
  ...props
}: FormSelectFieldProps<TFieldValues, TName>) => {
  const resolvedSelectProps = selectProps ?? {};
  const { isMulti: isMultiSelect = false, ...sharedSelectProps } = resolvedSelectProps;

  return (
    <FormControlledField
      {...props}
      renderInput={({ field }) => {
        const value = typeof field.value === "string" || Array.isArray(field.value) ? field.value : undefined;

        if (isMultiSelect) {
          return (
            <Select
              {...sharedSelectProps}
              isMulti
              isClearable={clearable}
              options={options ?? []}
              value={Array.isArray(value) ? value : []}
              placeholder={placeholder}
              onValueChange={(nextValue) => {
                const mappedValue = mapValueToField ? mapValueToField(nextValue, field) : nextValue;
                field.onChange(mappedValue);
              }}
            />
          );
        }

        const rawValue = field.value;
        const singleValue =
          typeof rawValue === "string" && rawValue.trim().length > 0 ? rawValue : undefined;

        return (
          <Select
            {...sharedSelectProps}
            isClearable={clearable}
            options={options ?? []}
            value={singleValue}
            placeholder={placeholder}
            onValueChange={(nextValue) => {
              const mappedValue = mapValueToField ? mapValueToField(nextValue, field) : nextValue;
              field.onChange(mappedValue);
            }}
          />
        );
      }}
    />
  );
};

type FormLookupSelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<FormControlledFieldProps<TFieldValues, TName>, "renderInput"> & {
  lookupProps?: Omit<LookupSelectProps<string>, "defaultValue" | "onValueChange" | "value">;
  mapValueToField?: (
    nextValue: string | string[],
    field: ControllerRenderProps<TFieldValues, TName>,
  ) => string | string[] | undefined;
};

const FormLookupSelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  lookupProps,
  mapValueToField,
  ...props
}: FormLookupSelectFieldProps<TFieldValues, TName>) => {
  const resolvedLookupProps = lookupProps ?? { options: [] };
  const { isMulti: isMultiLookup = false, ...sharedLookupProps } = resolvedLookupProps;

  return (
    <FormControlledField
      {...props}
      renderInput={({ field }) => {
        const value = typeof field.value === "string" || Array.isArray(field.value) ? field.value : undefined;

        if (isMultiLookup) {
          return (
            <LookupSelect
              {...sharedLookupProps}
              isMulti
              value={Array.isArray(value) ? value : []}
              onValueChange={(nextValue) => {
                const mappedValue = mapValueToField ? mapValueToField(nextValue, field) : nextValue;
                field.onChange(mappedValue);
              }}
            />
          );
        }

        const rawValue = field.value;
        const singleValue =
          typeof rawValue === "string" && rawValue.trim().length > 0 ? rawValue : undefined;

        return (
          <LookupSelect
            {...sharedLookupProps}
            value={singleValue}
            onValueChange={(nextValue) => {
              const mappedValue = mapValueToField ? mapValueToField(nextValue, field) : nextValue;
              field.onChange(mappedValue);
            }}
          />
        );
      }}
    />
  );
};

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
  FormFieldWrapper,
  FormControlledField,
  FormSelectField,
  FormLookupSelectField,
};

export type {
  FormControlledFieldProps,
  FormControlledFieldRenderProps,
  FormFieldWrapperProps,
  FormLookupSelectFieldProps,
  FormSelectFieldProps,
  FormSelectOption,
};
